import { useCallback, useEffect, useState } from "react";
import { Record, TransformData, useCheckMinimumRequiredProps, useDataProvider, useNotify, useRefresh, useSaveModifiers } from "react-admin";
import { findOne } from "../../../services/common/common";

const action = (dataProvider, resource, data, record?) => {
    if (data.id) {
        return dataProvider.update(resource, {
            id: data.id,
            data,
            previousData: record
        });
    } else {
        return dataProvider.create(resource, {
            data
        });
    }
};

export interface SimpleControllerProps<
    RecordType extends Omit<Record, 'id'> = Record
    > {
    basePath: string;
    save: (
        record: Partial<Record>,
        callbacks?: {
            transform?: TransformData;
        }
    ) => void;
    record: Partial<RecordType>;
    resource: string;
};

export const useSimpleController = <RecordType extends Record = Record>(
    props
): SimpleControllerProps<RecordType> => {
    useCheckMinimumRequiredProps('Simple', ['resource'], props);
    const {
        resource,
        basePath = `/${resource}`,
        onSuccess,
        onFailure,
        transform,
        mutationMode = "pessimistic",
        filter = {}
    } = props;
    const [record, setRecord] = useState(props.record || {});
    const notify = useNotify();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const reload = [];
    for (const key of Object.keys(filter)) {
        reload.push(filter[key]);
    }
    useEffect(() => {
        if (filter) {
            findOne(dataProvider, resource, filter).then((data) => {
                setRecord(data || {});
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataProvider, resource, ...reload]);
    const {
        transformRef,
    } = useSaveModifiers({ onSuccess, onFailure, transform });
    const save = useCallback(
        (
            data: Partial<Record>,
            { transform } = { transform: null }
        ) =>
            Promise.resolve(
                transform
                    ? transform(data)
                    : transformRef.current
                        ? transformRef.current(data)
                        : data
            ).then(data =>
                action(dataProvider, resource, data, record).then((result) => {
                    notify(
                        `ra.notification.${data.id ? "updated" : "created"}`,
                        {
                            type: 'info',
                            messageArgs: { smart_count: 1 },
                            undoable: mutationMode === 'undoable',
                        }
                    );
                    setRecord(result.data || {});
                }).catch(error => {
                    notify(
                        typeof error === 'string'
                            ? error
                            : error.message ||
                            'ra.notification.http_error',
                        {
                            type: 'warning',
                            messageArgs: {
                                _:
                                    typeof error === 'string'
                                        ? error
                                        : error && error.message
                                            ? error.message
                                            : undefined,
                            },
                        }
                    );
                    if (
                        mutationMode === 'undoable' ||
                        mutationMode === 'pessimistic'
                    ) {
                        refresh();
                    }
                })
            ),
        [
            transformRef,
            notify,
            refresh,
            mutationMode,
            record,
            dataProvider,
            resource
        ]
    );
    return {
        save,
        resource,
        basePath,
        record
    };
};